import React, { useCallback, useRef, useState } from 'react';
import '@wix/design-system/styles.global.css';
import { Box, WixDesignSystemProvider } from '@wix/design-system';
import s from './ChatWidget.module.scss';
import { AssistantChatMessage } from './components/ChatMessage/AssistantChatMessage/AssistantChatMessage';
import { UserChatMessage } from './components/ChatMessage/UserChatMessage';
import { createUserMessage } from './helpers';
import { wait } from './utils';
import { ChatMessageData, ChatMessageWidget, Theme } from './types';
import { UserResponseInput } from './components/UserResponseInput/UserResponseInput';
import { useAutoScroll } from './hooks/autoScroll';
import { chatWidgetDataHooks } from './ChatWidget.dataHooks';
import { useDataHooks } from '@wix/data-hooks-react';
import { ConsentModal } from './components/ChatMessageWidgets/ConsentModal';
import { OnWidgetAction } from '@wix/chat-widget-common-widgets';
import { ScrollDownButton } from './components/ScrollDownButton/ScrollDownButton';
import { Tags } from './components/Tags/Tags';
import { ITag } from './components/Tags/Tag';
import cs from 'classnames';

interface ChatWidgetProps {
  dataHook?: string;
  onTypingEnd?: () => void;
  onSendMessage?: (
    message: ChatMessageData,
    ctaType: 'buttonClick' | 'enterKeyPressed',
  ) => void;
  chatMessages?: ChatMessageData[];
  messageToSendPlaceholder?: string;
  isInputVisible?: boolean;
  widgets: ChatMessageWidget[];
  disabled?: boolean;
  userImage?: string;
  userDisplayName?: string;
  onResponseInputClick?: () => void;
  onResponseInputFocus?: () => void;
  onUserStartTyping?: (message: string) => void;
  theme?: Theme;
  statusMessage?: string;
  assistantName?: string;
  onWidgetAction?: OnWidgetAction;
  tags?: ITag[];
  onError?: (error: Error) => void;
}

const themes = {
  original: 'chat-theme-original',
  dark: 'chat-theme-dark',
  light: 'chat-theme-light',
};

const convertConsentMessageToEmptyAssistaneMessage = (
  message: ChatMessageData,
) => {
  return message.role === 'consentController'
    ? {
        role: 'assistant',
        content: '',
        id: message.id,
      }
    : message;
};
export const ChatWidget: React.FC<ChatWidgetProps> = ({
  dataHook,
  onTypingEnd = () => {},
  onSendMessage = () => {},
  chatMessages = [],
  messageToSendPlaceholder = 'Write your message',
  isInputVisible = true,
  widgets = [],
  disabled = false,
  userImage = '',
  userDisplayName = 'Me',
  onResponseInputClick = () => {},
  onResponseInputFocus = () => {},
  onUserStartTyping = () => {},
  theme = 'light',
  statusMessage = '',
  assistantName = 'AI Assistant',
  onWidgetAction = () => {},
  tags,
  onError = () => {},
}) => {
  const dataHooks = useDataHooks(chatWidgetDataHooks, dataHook);
  const [answer, setAnswer] = useState<string>('');
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [isUserStartTyping, setIsUserStartTyping] = useState(false);
  const scrollingDivRef = useRef(null);
  const { isScrolledDown, scrollToBottom, autoScroll } = useAutoScroll(
    chatMessages,
    scrollingDivRef,
  );

  const canSend = !!answer && !isWaitingForResponse && !disabled;

  const sendMessageHandler = useCallback(
    (ctaType: 'buttonClick' | 'enterKeyPressed') => {
      if (answer.trim() && canSend) {
        setIsWaitingForResponse(true);
        setAnswer('');
        onSendMessage(createUserMessage(answer), ctaType);
        setIsUserStartTyping(false);
      }
    },
    [answer, canSend, onSendMessage],
  );

  const onTypingEndHandler = async () => {
    setIsWaitingForResponse(false);
    onTypingEnd();
    await wait(500);
  };

  const onResponseInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setAnswer(e.target.value);
    if (!isUserStartTyping) {
      setIsUserStartTyping(true);
      onUserStartTyping(e.target.value);
    }
  };

  return (
    <div data-theme={themes[theme]} className={s.variablesHolder}>
      <WixDesignSystemProvider
        features={{ newColorsBranding: true }}
        as="div"
        className={s.rootWrapper}
      >
        <Box
          className={s.root}
          direction="vertical"
          dataHook={dataHooks.base()}
        >
          <div
            className={s.chatContent}
            ref={scrollingDivRef}
            data-hook="scrollableChatElement"
          >
            {chatMessages
              .map(convertConsentMessageToEmptyAssistaneMessage)
              .map((message, index) => (
                <div
                  data-content={message.content}
                  key={message.id || `message-${index}`}
                >
                  {message.role === 'assistant' ? (
                    <AssistantChatMessage
                      dataHook={dataHooks.assistantMessage({ index })}
                      content={message.content}
                      onTypingEnd={() => onTypingEndHandler()}
                      onNewLine={autoScroll}
                      assistantName={assistantName}
                      widgets={widgets}
                      index={index}
                      statusMessage={statusMessage}
                      theme={theme}
                      isLast={index === chatMessages.length - 1}
                      onWidgetAction={onWidgetAction}
                      onError={onError}
                    />
                  ) : (
                    <UserChatMessage
                      dataHook={dataHooks.userChatMessage({ index })}
                      content={message.content}
                      userDisplayName={userDisplayName}
                      userImage={userImage}
                      theme={theme}
                    />
                  )}
                </div>
              ))}
          </div>
          <Box className={s.space}></Box>

          <div
            className={cs(s.scrollDownButton, {
              [s.scrollDownButtonHidden]: isScrolledDown,
            })}
            onClick={scrollToBottom}
          >
            <ScrollDownButton theme={theme} />
          </div>

          {tags?.length && (
            <div className={s.tags} data-hook={dataHooks.tags()}>
              <Tags tags={tags} theme={theme} disabled={disabled} />
            </div>
          )}

          <Box direction="vertical">
            {isInputVisible && (
              <Box className={s.inputWrapper}>
                <Box direction="vertical" align="center" flex={1}>
                  <UserResponseInput
                    placeholder={messageToSendPlaceholder}
                    onChange={onResponseInputChange}
                    onSend={sendMessageHandler}
                    value={answer}
                    inputDisabled={disabled}
                    sendButtonDisabled={!canSend}
                    onClick={onResponseInputClick}
                    onFocus={onResponseInputFocus}
                  />
                </Box>
              </Box>
            )}
            <ConsentModal chatMessages={chatMessages} />
          </Box>
        </Box>
      </WixDesignSystemProvider>
    </div>
  );
};
